/**
 * Reading the timing records of the open document (W2-08 timing): a snapshot view for React (`timingView`, used
 * with the version topics of `TIMING_TOPICS`), the tempo map and meter of a store (cached per version), and the
 * playhead as a beat.
 */

import type { AppServices } from '../../app/registry/types';
import { buildTempoMap } from '../../core/doc/container';
import type { AssetRef, BpmPoint, ChartDoc, DocIndexes, MeterMark } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import { createMeterMap, type MeterMap } from '../../core/time/meter';
import type { Beat, Sec, TempoMap } from '../../core/time/types';
import type { DocStore, VersionTopic } from '../../state/types';
import { decodedShiftSec, heardOffsetMs } from './timingModel';

/** Version topics the timing view depends on. */
export const TIMING_TOPICS: readonly VersionTopic[] = ['bpm', 'timing', 'pkg', 'editor', 'meta', 'assets'];

export interface TimingView {
  readonly points: readonly BpmPoint[];
  readonly meter: readonly MeterMark[];
  readonly offsetMs: number;
  readonly infoOffsetSec: number;
  readonly heardMs: number;
  /** Decoded music time of beat 0 (s). */
  readonly shiftSec: number;
  readonly previewAudioDelayMs: number;
  readonly confirmed: boolean;
  readonly music: AssetRef | null;
  readonly title: string;
  readonly artist: string;
  readonly tempo: TempoMap;
  readonly meterMap: MeterMap;
}

const viewCache = new WeakMap<object, { readonly key: string; readonly view: TimingView }>();

/** A snapshot of the timing records (same object while nothing it reads changed). */
export function timingView(doc: ChartDoc, idx: DocIndexes): TimingView {
  const points = idx
    .bpmSorted()
    .map((id) => doc.bpm.get(id))
    .filter((p): p is BpmPoint => p !== undefined);
  const music = doc.meta.music === null ? null : (doc.assets.get(doc.meta.music) ?? null);
  const key = [
    points.map((p) => `${Beats.key(p.t)}=${String(p.bpm)}#${String(p.id)}`).join(','),
    doc.timing.meter.map((m) => `${Beats.key(m.t)}=${String(m.beatsPerBar)}`).join(','),
    doc.timing.offsetMs,
    doc.timing.confirmed,
    doc.pkg.infoOffsetSec,
    doc.editor.previewAudioDelayMs,
    music?.id ?? '',
    doc.meta.name,
    doc.meta.composer,
  ].join('|');
  const hit = viewCache.get(doc);
  if (hit?.key === key) return hit.view;
  const heardMs = heardOffsetMs(doc.timing, doc.pkg);
  const view: TimingView = {
    points,
    meter: doc.timing.meter,
    offsetMs: doc.timing.offsetMs,
    infoOffsetSec: doc.pkg.infoOffsetSec,
    heardMs,
    shiftSec: decodedShiftSec(heardMs, doc.editor.previewAudioDelayMs),
    previewAudioDelayMs: doc.editor.previewAudioDelayMs,
    confirmed: doc.timing.confirmed,
    music,
    title: doc.meta.name,
    artist: doc.meta.composer,
    tempo: buildTempoMap(doc, idx),
    meterMap: createMeterMap(doc.timing.meter),
  };
  viewCache.set(doc, { key, view });
  return view;
}

/** The current timing view of a store. */
export function viewOf(store: Pick<DocStore, 'doc' | 'idx'>): TimingView {
  return timingView(store.doc, store.idx);
}

/** The tempo map of a store. */
export function tempoOf(store: Pick<DocStore, 'doc' | 'idx'>): TempoMap {
  return viewOf(store).tempo;
}

/** Chart seconds of the playhead (the transport's visual clock). */
export function playheadSec(services: Pick<AppServices, 'audio'>): number {
  return services.audio.transport.chartTime();
}

/** The playhead as a beat, snapped to the beat snap when it is on (1/1920 otherwise). */
export function playheadBeat(services: Pick<AppServices, 'audio' | 'editor' | 'store'>): Beat {
  const store = services.store;
  if (store === null) return Beats.ZERO;
  const snap = services.editor.getState().snap;
  const exact = tempoOf(store).beatAt(Math.max(0, playheadSec(services)));
  const b = snap.beatSnap ? Beats.snap(exact, Math.max(1, Math.min(1920, Math.round(snap.div)))) : exact;
  return Beats.cmp(b, Beats.ZERO) < 0 ? Beats.ZERO : b;
}

/** A metronome's click times (chart seconds) with their bar accents, over [t0, t1). */
export type MetronomeBeats = (t0: Sec, t1: Sec) => Array<{ readonly t: Sec; readonly accent: boolean }>;

/**
 * Metronome clicks of the open document: one per whole beat from beat 0, accented on the bar lines of its meter
 * (what the shell installs while the editor's metronome is on).
 */
export function chartMetronomeBeats(store: Pick<DocStore, 'doc' | 'idx'>): MetronomeBeats {
  const view = viewOf(store);
  const tempo = view.tempo;
  const meter = view.meterMap;
  return (t0, t1) => {
    const out: Array<{ readonly t: Sec; readonly accent: boolean }> = [];
    if (!(t1 > t0)) return out;
    const b0 = Math.max(0, Math.ceil(tempo.beatF64At(Math.max(0, t0)) - 1e-9));
    const b1 = tempo.beatF64At(t1);
    for (let b = b0; b < b1 && out.length < 4096; b++) {
      const t = tempo.secAtBeatF64(b);
      if (t < t0 || t >= t1) continue;
      out.push({ t, accent: meter.isBarStart(Beats.fromInt(b)) });
    }
    return out;
  };
}

/** Puts the metronome back to what the editor shows: the chart's clicks while its metronome is on, else none. */
export function restoreEditorMetronome(services: Pick<AppServices, 'audio' | 'editor' | 'store'>): void {
  const store = services.store;
  if (store !== null && services.editor.getState().metronome) {
    services.audio.setMetronome(true, chartMetronomeBeats(store));
  } else {
    services.audio.setMetronome(false, () => []);
  }
}

/** "12:1/4" style text of a beat. */
export function beatText(b: Beat): string {
  const t = Beats.toTriple(b);
  return t[1] === 0 ? String(t[0]) : `${String(t[0])}:${String(t[1])}/${String(t[2])}`;
}
